import type { AnalyticsEvents } from '@sunflower-games/analytics-events';

import { INTERACTIVE_GUIDE_EVENTS_ENUM } from '../constants';

export const riveFTUELoadingExpirationTime = 10000; // 10 seconds

export enum RIVE_FTUE_STATE_EVENTS {
  ENTRY = 'entry',
  ENTRY_LOOP = 'entry_loop',
}

export enum RIVE_FTUE_EVENTS {
  WELCOME_1 = '1_welcome',
  GAME_MODES = '2_2_game_modes',
  GAME_MODES_DESKTOP = '2_game_modes',
  CC_MODE = '3_cc_mode',
  SLOT = '4_slot',
  START_SPIN = 'start spin', // appear multiple times
  STOP_SPIN = 'stop spin',
  FIRST_CC_SPIN = '5_spin_1_cc',
  SECOND_CC_SPIN = '6_spin_2_cc',
  TOGGLE_SWITCH = '7_toggle_switch',
  SC_MODE = '8_sc_mode',
  FIRST_SC_SPIN = '9_spin_1_sc',
  SECOND_SC_SPIN = '10_spin_2_sc',
  THIRD_SC_SPIN = '11_spin_3_sc',
  HOME = '12_home',
  REDEEM = '13_redeem',
  REDEEM_CONVERSION = '14_1$',
  STORE = '15_store',
  ALL_SET = '16_all_set',
  FINISH = 'finish',

  // desktop uniq events
  MENU = '13_menu',
  REDEEM_DESKTOP = '14_redeem',
  REDEEM_CONVERSION_DESKTOP = '15_1$',
  STORE_DESKTOP = '16_store',
  ALL_SET_DESKTOP = '17_all_set',
}

export const riveFTUEEventsMap: Record<RIVE_FTUE_EVENTS, INTERACTIVE_GUIDE_EVENTS_ENUM | 'ignorable_event'> = {
  [RIVE_FTUE_EVENTS.WELCOME_1]: INTERACTIVE_GUIDE_EVENTS_ENUM.WELCOME,
  [RIVE_FTUE_EVENTS.GAME_MODES]: INTERACTIVE_GUIDE_EVENTS_ENUM.GAME_MODES,
  [RIVE_FTUE_EVENTS.GAME_MODES_DESKTOP]: INTERACTIVE_GUIDE_EVENTS_ENUM.GAME_MODES,
  [RIVE_FTUE_EVENTS.CC_MODE]: INTERACTIVE_GUIDE_EVENTS_ENUM.CC_MODE,
  [RIVE_FTUE_EVENTS.SLOT]: INTERACTIVE_GUIDE_EVENTS_ENUM.SLOT,
  [RIVE_FTUE_EVENTS.START_SPIN]: 'ignorable_event',
  [RIVE_FTUE_EVENTS.STOP_SPIN]: 'ignorable_event',
  [RIVE_FTUE_EVENTS.FIRST_CC_SPIN]: INTERACTIVE_GUIDE_EVENTS_ENUM.FIRST_CC_SPIN,
  [RIVE_FTUE_EVENTS.SECOND_CC_SPIN]: INTERACTIVE_GUIDE_EVENTS_ENUM.SECOND_CC_SPIN,
  [RIVE_FTUE_EVENTS.TOGGLE_SWITCH]: INTERACTIVE_GUIDE_EVENTS_ENUM.TOGGLE_SWITCH,
  [RIVE_FTUE_EVENTS.SC_MODE]: INTERACTIVE_GUIDE_EVENTS_ENUM.SC_MODE,
  [RIVE_FTUE_EVENTS.FIRST_SC_SPIN]: INTERACTIVE_GUIDE_EVENTS_ENUM.FIRST_SC_SPIN,
  [RIVE_FTUE_EVENTS.SECOND_SC_SPIN]: INTERACTIVE_GUIDE_EVENTS_ENUM.SECOND_SC_SPIN,
  [RIVE_FTUE_EVENTS.THIRD_SC_SPIN]: INTERACTIVE_GUIDE_EVENTS_ENUM.THIRD_SC_SPIN,
  [RIVE_FTUE_EVENTS.HOME]: INTERACTIVE_GUIDE_EVENTS_ENUM.HOME,
  [RIVE_FTUE_EVENTS.REDEEM]: INTERACTIVE_GUIDE_EVENTS_ENUM.REDEEM,
  [RIVE_FTUE_EVENTS.REDEEM_CONVERSION]: INTERACTIVE_GUIDE_EVENTS_ENUM.REDEEM_CONVERSION,
  [RIVE_FTUE_EVENTS.STORE]: INTERACTIVE_GUIDE_EVENTS_ENUM.STORE,
  [RIVE_FTUE_EVENTS.ALL_SET]: INTERACTIVE_GUIDE_EVENTS_ENUM.ALL_SET,
  [RIVE_FTUE_EVENTS.FINISH]: INTERACTIVE_GUIDE_EVENTS_ENUM.FINISH,

  // desktop events
  [RIVE_FTUE_EVENTS.MENU]: INTERACTIVE_GUIDE_EVENTS_ENUM.DESKTOP_MENU,
  [RIVE_FTUE_EVENTS.REDEEM_DESKTOP]: INTERACTIVE_GUIDE_EVENTS_ENUM.REDEEM,
  [RIVE_FTUE_EVENTS.REDEEM_CONVERSION_DESKTOP]: INTERACTIVE_GUIDE_EVENTS_ENUM.REDEEM_CONVERSION,
  [RIVE_FTUE_EVENTS.STORE_DESKTOP]: INTERACTIVE_GUIDE_EVENTS_ENUM.STORE,
  [RIVE_FTUE_EVENTS.ALL_SET_DESKTOP]: INTERACTIVE_GUIDE_EVENTS_ENUM.ALL_SET,
};

export const defaultAnalyticsParams = {
  event_category: 'tutorial',
  event_action: 'impression',
} as const;

export const interactiveGuideAnalyticsParamsMap: Record<
  string,
  {
    event_name: keyof AnalyticsEvents;
    step_name: string;
  }
> = {
  [INTERACTIVE_GUIDE_EVENTS_ENUM.WELCOME]: {
    event_name: 'welcome',
    step_name: 'step_1_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.GAME_MODES]: {
    event_name: 'game_modes',
    step_name: 'step_2_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.CC_MODE]: {
    event_name: 'cc_mode',
    step_name: 'step_3_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.SLOT]: {
    event_name: 'choose_slot',
    step_name: 'step_4_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.FIRST_CC_SPIN]: {
    event_name: 'first_cc_spin',
    step_name: 'step_5_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.SECOND_CC_SPIN]: {
    event_name: 'second_cc_spin',
    step_name: 'step_6_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.TOGGLE_SWITCH]: {
    event_name: 'toggle_switch',
    step_name: 'step_7_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.SC_MODE]: {
    event_name: 'sc_mode',
    step_name: 'step_8_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.FIRST_SC_SPIN]: {
    event_name: 'first_sc_spin',
    step_name: 'step_9_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.SECOND_SC_SPIN]: {
    event_name: 'second_sc_spin',
    step_name: 'step_10_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.THIRD_SC_SPIN]: {
    event_name: 'third_sc_spin',
    step_name: 'step_11_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.HOME]: {
    event_name: 'back_to_lobby',
    step_name: 'step_12_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.DESKTOP_MENU]: {
    event_name: 'menu_clicking',
    step_name: 'step_12_2_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.REDEEM]: {
    event_name: 'redeem_center',
    step_name: 'step_13_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.REDEEM_CONVERSION]: {
    event_name: 'redeem_real_prizes',
    step_name: 'step_14_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.STORE]: {
    event_name: 'store_more_coins',
    step_name: 'step_15_tutorial',
  },
  [INTERACTIVE_GUIDE_EVENTS_ENUM.ALL_SET]: {
    event_name: 'all_set_tutorial',
    step_name: 'step_16_tutorial',
  },
};

export const defaultConfig = {
  desktop: {
    stateMachineName: 'State Machine 1',
    url: 'https://static.crowncoinscasino.com/production/assets/rive/ftue/ftue_desktop_english_08_12_25.riv',
    skip_tutorial: 1,
  },
  ftue_type: 'interactiveAnimation',
  mobile_m: {
    stateMachineName: 'State Machine 1',
    url: 'https://static.crowncoinscasino.com/production/assets/rive/ftue/ftue_mobile_english_08_12_25.riv',
    skip_tutorial: 1,
  },
  mobile_web: {
    stateMachineName: 'State Machine 1',
    url: 'https://static.crowncoinscasino.com/production/assets/rive/ftue/ftue_mobile_english_08_12_25.riv',
    skip_tutorial: 1,
  },
  tablet: {
    stateMachineName: 'State Machine 1',
    url: '',
    skip_tutorial: 1,
  },
};
